import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, ArrowLeft, Save, Loader2, Trash2, CheckSquare, Square, X as XIcon, Calendar } from "lucide-react";
import RichText from "@/components/common/RichText";
import ImageUpload from "@/components/common/ImageUpload";

const CATEGORIES = ["News", "Reviews", "Buying Guide", "EV", "Tech", "Comparison", "Launch"];

const empty = {
  title: "", slug: "", excerpt: "", body: "",
  hero_image: "", category: "News", tags: [],
  author_name: "TrendingNewCar Editorial", author_slug: "editorial",
  related_car_slugs: [], related_article_slugs: [],
  status: "draft",
  published_at: new Date().toISOString(),
  read_minutes: 3,
  meta_title: "", meta_description: "", canonical_url: "", og_image: "",
};

const slugify = (s) => s.toString().toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, "")
  .replace(/\s+/g, "-").replace(/-+/g, "-");

const toLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};
const fromLocalInput = (v) => v ? new Date(v).toISOString() : new Date().toISOString();

export default function ArticlesAdmin() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 20;
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const debounceRef = useRef(null);

  const load = () => {
    setBusy(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (categoryFilter) params.set("category", categoryFilter);
    if (statusFilter) params.set("status", statusFilter);
    params.set("limit", String(perPage));
    params.set("skip", String((page - 1) * perPage));
    api.get(`/admin/articles?${params.toString()}`)
      .then((r) => { setRows(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [q, categoryFilter, statusFilter, page]);

  const openNew = () => setEditing({ ...empty });
  const openEdit = async (slug) => {
    try {
      const { data } = await api.get(`/admin/articles/${slug}`);
      setEditing({ ...data, _existing: true });
    } catch { toast.error("Could not load article"); }
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.title || !editing.slug) return toast.error("title and slug are required");
    if (!editing.excerpt) return toast.error("excerpt is required");
    if (!editing.hero_image) return toast.error("Featured image is required");
    try {
      const payload = { ...editing };
      delete payload._existing;
      if (editing._existing) {
        await api.put(`/admin/articles/${editing.slug}`, payload);
      } else {
        await api.post("/admin/articles", payload);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (slug) => {
    if (!window.confirm(`Delete article "${slug}"?`)) return;
    await api.delete(`/admin/articles/${slug}`);
    load();
  };

  const toggle = (slug) => {
    const next = new Set(selected);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.slug)));
  };
  const bulkStatus = async (status) => {
    if (!selected.size) return toast.error("Select at least one article");
    try {
      const { data } = await api.post("/admin/articles/bulk/status", { slugs: [...selected], status });
      toast.success(`Updated ${data.modified} → ${status}`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk update failed"); }
  };
  const bulkDelete = async () => {
    if (!selected.size) return toast.error("Select at least one article");
    if (!window.confirm(`Delete ${selected.size} article(s)?`)) return;
    try {
      const { data } = await api.post("/admin/articles/bulk/delete", { slugs: [...selected] });
      toast.success(`Deleted ${data.deleted} articles`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const allChecked = rows.length > 0 && selected.size === rows.length;

  if (editing) {
    return (
      <div data-testid="admin-articles-edit">
        <button onClick={() => setEditing(null)} className="text-sm text-blue-600 inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4"/> Back to articles
        </button>
        <h1 className="font-display text-3xl tracking-tighter">{editing._existing ? "Edit article" : "New article"}</h1>
        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Field label="Title *">
              <input data-testid="article-title" value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing._existing ? editing.slug : slugify(e.target.value) })}
                className={inp}/>
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Slug *">
                <input data-testid="article-slug" value={editing.slug} disabled={editing._existing}
                  onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
                  className={inp + (editing._existing ? " opacity-60" : "")}/>
              </Field>
              <Field label="Read minutes">
                <input data-testid="article-read-mins" type="number" min="1" max="60" value={editing.read_minutes || 3}
                  onChange={(e) => setEditing({ ...editing, read_minutes: parseInt(e.target.value || "3", 10) })}
                  className={inp}/>
              </Field>
            </div>
            <Field label="Excerpt *">
              <textarea data-testid="article-excerpt" value={editing.excerpt}
                onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                className={`${inp} min-h-[80px] py-2`} placeholder="A one-liner shown on cards and search results"/>
            </Field>
            <Field label="Body">
              <RichText testId="article-body" value={editing.body} onChange={(html) => setEditing({ ...editing, body: html })}
                placeholder="Write the article body…"/>
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Author name">
                <input data-testid="article-author" value={editing.author_name || ""}
                  onChange={(e) => setEditing({ ...editing, author_name: e.target.value })} className={inp}/>
              </Field>
              <Field label="Author slug">
                <input data-testid="article-author-slug" value={editing.author_slug || ""}
                  onChange={(e) => setEditing({ ...editing, author_slug: slugify(e.target.value) })} className={inp}/>
              </Field>
            </div>
          </div>

          <div className="space-y-4">
            <Field label="Featured image *">
              <ImageUpload value={editing.hero_image} onChange={(v) => setEditing({ ...editing, hero_image: v })} folder="articles" testId="article-hero"/>
            </Field>

            <div className="rounded-3xl border border-[#E5E7EB] bg-white p-4 space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Publish</div>
              <Field label="Status">
                <select data-testid="article-status" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className={inp}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </Field>
              <Field label="Publish at">
                <div className="relative">
                  <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"/>
                  <input data-testid="article-publish-at" type="datetime-local" value={toLocalInput(editing.published_at)}
                    onChange={(e) => setEditing({ ...editing, published_at: fromLocalInput(e.target.value) })}
                    className={`${inp} pl-9`}/>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">Future date + status &quot;Scheduled&quot; auto-publishes at that time.</div>
              </Field>
            </div>

            <div className="rounded-3xl border border-[#E5E7EB] bg-white p-4 space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Taxonomy</div>
              <Field label="Category">
                <select data-testid="article-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inp}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <TagsEditor label="Tags" testId="article-tags" values={editing.tags || []} onChange={(v) => setEditing({ ...editing, tags: v })}/>
            </div>

            <div className="rounded-3xl border border-[#E5E7EB] bg-white p-4 space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">SEO</div>
              <Field label="Meta title">
                <input data-testid="article-meta-title" value={editing.meta_title || ""}
                  onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })} className={inp}/>
              </Field>
              <Field label="Meta description">
                <textarea data-testid="article-meta-desc" value={editing.meta_description || ""}
                  onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} className={`${inp} min-h-[70px] py-2`}/>
              </Field>
              <Field label="Canonical URL">
                <input data-testid="article-canonical" value={editing.canonical_url || ""}
                  onChange={(e) => setEditing({ ...editing, canonical_url: e.target.value })} className={inp}/>
              </Field>
              <Field label="OG image">
                <ImageUpload value={editing.og_image} onChange={(v) => setEditing({ ...editing, og_image: v })} folder="articles/og" testId="article-og"/>
              </Field>
            </div>

            <button onClick={save} data-testid="article-save" className="btn-primary w-full h-11 text-sm inline-flex items-center justify-center gap-2">
              <Save className="w-4 h-4"/> Save article
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-articles">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tighter">Articles</h1>
          <p className="text-sm text-muted-foreground mt-1">News, reviews, buying guides & EV articles with TipTap editor and SEO.</p>
        </div>
        <button data-testid="article-new" onClick={openNew} className="btn-primary h-10 px-4 text-sm inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> New article
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-[#E5E7EB] bg-white p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <input data-testid="article-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search title, slug, excerpt…"
            className="h-10 pl-9 pr-3 rounded-full border border-[#E5E7EB] bg-white text-sm outline-none focus:border-blue-400 w-full"/>
        </div>
        <select data-testid="article-filter-category" value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }} className="h-10 px-3 rounded-full border border-[#E5E7EB] bg-white text-sm">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="article-filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="h-10 px-3 rounded-full border border-[#E5E7EB] bg-white text-sm">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="scheduled">Scheduled</option>
        </select>
        <div className="text-xs text-muted-foreground px-2">Selected: <span className="font-semibold text-[#1F2937]" data-testid="articles-sel-count">{selected.size}</span></div>
        <button data-testid="articles-bulk-publish" onClick={() => bulkStatus("published")} disabled={!selected.size}
          className="h-10 px-3 rounded-full border border-[#E5E7EB] bg-white text-sm disabled:opacity-50 hover:bg-[#F3F6FA]">Publish</button>
        <button data-testid="articles-bulk-unpublish" onClick={() => bulkStatus("draft")} disabled={!selected.size}
          className="h-10 px-3 rounded-full border border-[#E5E7EB] bg-white text-sm disabled:opacity-50 hover:bg-[#F3F6FA]">Draft</button>
        <button data-testid="articles-bulk-delete" onClick={bulkDelete} disabled={!selected.size}
          className="h-10 px-3 rounded-full border border-rose-200 text-rose-600 bg-white text-sm inline-flex items-center gap-1 disabled:opacity-50 hover:bg-rose-50">
          <Trash2 className="w-3.5 h-3.5"/> Delete
        </button>
      </div>

      <div className="mt-4 rounded-3xl border border-[#E5E7EB] overflow-hidden bg-white">
        {busy ? (
          <div className="grid place-items-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin"/></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground text-sm">No articles match your filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="p-3 w-10"><button data-testid="articles-select-all" onClick={toggleAll}>{allChecked ? <CheckSquare className="w-4 h-4 text-[#2563EB]"/> : <Square className="w-4 h-4 text-muted-foreground"/>}</button></th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Title</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Category</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Publish</th>
                <th className="p-3"/>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const on = selected.has(a.slug);
                return (
                  <tr key={a.slug} data-testid={`article-row-${a.slug}`} className={`border-t border-[#E5E7EB] ${on ? "bg-blue-50/40" : ""}`}>
                    <td className="p-3">
                      <button data-testid={`article-select-${a.slug}`} onClick={() => toggle(a.slug)}>
                        {on ? <CheckSquare className="w-4 h-4 text-[#2563EB]"/> : <Square className="w-4 h-4 text-muted-foreground"/>}
                      </button>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        {a.hero_image ? (
                          <img src={a.hero_image} alt="" className="w-12 h-8 rounded object-cover border border-[#E5E7EB]"/>
                        ) : (
                          <div className="w-12 h-8 rounded bg-[#F3F6FA] border border-[#E5E7EB]"/>
                        )}
                        <div>
                          <div className="font-medium">{a.title}</div>
                          <div className="text-xs text-muted-foreground">{a.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-muted-foreground">{a.category}</td>
                    <td className="p-3">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                        a.status === "published" ? "bg-emerald-50 text-emerald-700"
                        : a.status === "scheduled" ? "bg-amber-50 text-amber-700"
                        : "bg-secondary text-muted-foreground"
                      }`}>{a.status}</span>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{a.published_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(a.slug)} data-testid={`article-edit-${a.slug}`} className="text-sm text-blue-600 hover:underline mr-3">Edit</button>
                      <button onClick={() => del(a.slug)} data-testid={`article-del-${a.slug}`} className="text-sm text-rose-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="text-muted-foreground">Page {page} of {totalPages} · {total} articles</div>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="article-prev"
            className="h-9 px-3 rounded-full border border-[#E5E7EB] bg-white disabled:opacity-50">Prev</button>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="article-next"
            className="h-9 px-3 rounded-full border border-[#E5E7EB] bg-white disabled:opacity-50">Next</button>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#2563EB]";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function TagsEditor({ label, values, onChange, testId }) {
  const [text, setText] = useState("");
  const add = () => {
    const t = text.trim();
    if (!t) return;
    if ((values || []).includes(t)) { setText(""); return; }
    onChange([...(values || []), t]);
    setText("");
  };
  return (
    <Field label={label}>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(values || []).map((t, i) => (
          <span key={`${t}-${i}`} className="inline-flex items-center gap-1 h-7 pl-3 pr-1 rounded-full bg-[#EEF2F7] text-xs text-[#1F2937]">
            {t}
            <button type="button" onClick={() => onChange(values.filter((_, ix) => ix !== i))}
              className="h-5 w-5 rounded-full grid place-items-center hover:bg-white/60" data-testid={`${testId}-remove-${t}`}>
              <XIcon className="w-3 h-3"/>
            </button>
          </span>
        ))}
      </div>
      <input data-testid={`${testId}-input`} value={text} onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder="Type a tag and press Enter"
        className="w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#2563EB]"/>
    </Field>
  );
}
